import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import {
	AppModalService,
	ModalData,
} from '@datachain/ui-sdk/components/app-modal';
import { AppTagConfiguratorComponent } from '@datachain/ui-sdk/components/app-tag-configurator';
import { AppTagItemEntity } from '@datachain/ui-sdk/components/dc-types';
import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { RowDraggingReorderEvent } from 'devextreme/ui/data_grid';
import { ValueChangedEvent } from 'devextreme/ui/select_box';
import { ValidationResult } from 'devextreme/ui/validator';
import { Map, OrderedSet } from 'immutable';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	firstValueFrom,
	map,
	Observable,
	ReplaySubject,
	takeUntil,
	tap,
} from 'rxjs';
import { DcIcons } from 'src/app/ui/app-dc.icons';
import { DcLoaders } from 'src/app/ui/app-loaders';
import { AppMetadataComponent } from 'src/app/ui/components/app-metadata/app-metadata.component';
import { AppMetadataEntity } from 'src/app/ui/components/app-metadata/app-metadata.entity';

import {
	AvailableNumberFormats,
	DcTimestampFormat,
	DefaultDateFormat,
	DefaultNumberFormat,
	DefaultTimezone,
} from '../../../core/dc-core-types';
import {
	DefaultLanguage,
	SupportedLanguageEntity,
} from '../../domain/supported-language.entity';
import {
	createEntityFromDepot,
	DepotSelector,
	initAvailableDepotHeadersForEntityCreation,
} from '../../store';
import { DepotHeaderItemEntity } from './depot-header-item.entity';
import { DepotHeaderListEntity } from './depot-header-list.entity';

enum HeaderErrorKey {
	NoHeaderSelected = 'no-header-selected',
}

export type HeaderListError = {
	key: HeaderErrorKey;
};

@Component({
	selector: 'app-depot-entity-modal',
	templateUrl: './create-entity-from-depot-modal.component.html',
	styleUrls: ['./create-entity-from-depot-modal.component.scss'],
})
export class CreateEntityFromDepotModalComponent<
		T extends { depotId: number; depotType: string; depotLabel: string }
	>
	extends DcBaseComponent
	implements OnInit
{
	protected readonly HeaderErrorKey = HeaderErrorKey;

	protected readonly ColumnType = ColumnType;
	protected readonly DcIcons = DcIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly AvailableNumberFormats = AvailableNumberFormats;

	public vo$!: Observable<{
		isFirstLoad: boolean;
		contentReady: boolean;
		isLoading: boolean;
		isSaveDisabled: boolean;
		headersForSelectRepo: DepotHeaderListEntity;
		availableTags: OrderedSet<AppTagItemEntity>;
		listErrors: Map<HeaderErrorKey, HeaderListError>;
		availableDateFormats: Map<string, DcTimestampFormat>;
		availableTimezones: Array<string>;
		supportedLanguages: Map<string, SupportedLanguageEntity>;
	}>;

	public metadata: AppMetadataEntity = AppMetadataEntity.build({
		title: `entity__${this.modalData.depotLabel}`,
	});
	@ViewChild(AppMetadataComponent)
	public templateMetadataCmp!: AppMetadataComponent;

	@ViewChild(AppTagConfiguratorComponent)
	public tagConfiguratorComponentCmp: AppTagConfiguratorComponent | null = null;

	public dropDownOptions = {
		container: '.app-modal',
	};

	public hideAllSelectToggle = false;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);
	private readonly readySubject$ = new BehaviorSubject<boolean>(false);
	private readonly availableHeadersSubject =
		new ReplaySubject<DepotHeaderListEntity>(1);

	private readonly isMetadataErroredSubject = new BehaviorSubject<boolean>(
		false
	);

	private readonly isLabelErroredSubject = new BehaviorSubject<
		Map<
			number,
			{
				key: number;
			}
		>
	>(Map());

	private readonly headerListErrorsSubject = new BehaviorSubject<
		Map<HeaderErrorKey, HeaderListError>
	>(Map());

	public onReorder = async (e: RowDraggingReorderEvent): Promise<void> => {
		const visibleRows = e.component.getVisibleRows();
		const toId: number = visibleRows[e.toIndex].data.id;
		const fromId: number = e.itemData.id;

		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);
		const updated = currentHeaders.reorder(fromId, toId);
		this.availableHeadersSubject.next(updated);
	};

	public constructor(
		private readonly store: Store,
		private readonly depotSelector: DepotSelector,
		private readonly modalService: AppModalService,
		@Inject(MAT_DIALOG_DATA) public readonly modalData: ModalData & T
	) {
		super();
		this.cmpId = 'depot-entity';
		this.dxLocalStorageKey = 'dx.grid.depot-entity-columns-config';

		const detectErrors$ = combineLatest([
			this.isMetadataErroredSubject,
			this.isLabelErroredSubject,
			this.headerListErrorsSubject,
		]).pipe(
			map(
				([
					isMetadataErroredSubject,
					isLabelErroredSubject,
					headerListErrorsSubject,
				]) =>
					isMetadataErroredSubject ||
					isLabelErroredSubject.size !== 0 ||
					headerListErrorsSubject.size !== 0
			)
		);

		this.depotSelector
			.getHeadersForSelectRepo$()
			.pipe(
				takeUntil(this.onDestroy$),
				tap((list) => this.availableHeadersSubject.next(list))
			)
			.subscribe();

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			contentReady: this.readySubject$.pipe(debounceTime(2)),
			isLoading: depotSelector.getLoading$(),
			headersForSelectRepo: this.availableHeadersSubject,
			availableTags: depotSelector.getAvailableTags$(),
			isSaveDisabled: detectErrors$,
			listErrors: this.headerListErrorsSubject,
			availableDateFormats: this.depotSelector.getAvailableDateFormats$(),
			availableTimezones: this.depotSelector.getAvailableTimezones$(),
			supportedLanguages: this.depotSelector.getSupportedLanguages$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();

		this.store.dispatch(
			initAvailableDepotHeadersForEntityCreation({
				depotId: this.modalData.depotId,
				depotType: this.modalData.depotType,
			})
		);
	}

	public async onSave(): Promise<void> {
		let tags = new Array<AppTagItemEntity>();

		if (this.tagConfiguratorComponentCmp) {
			tags = await this.tagConfiguratorComponentCmp.getUsedTags();
		}

		const metadata = {
			...this.templateMetadataCmp.extractFormData(),
			tags: OrderedSet<AppTagItemEntity>(tags),
		} as AppMetadataEntity;

		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);

		// Get selected header elements
		const selectedHeaderElements = currentHeaders.elements.filter(
			(elem) => elem.isSelected
		);
		this.store.dispatch(
			createEntityFromDepot({
				depotId: this.modalData.depotId,
				metadata,
				headers: selectedHeaderElements.toList().toArray(),
			})
		);
	}

	public close(): void {
		this.modalService.closeActiveModal(undefined);
	}

	public async onUpdateLabel(
		key: number,
		event: ValidationResult
	): Promise<void> {
		const errorsLabels = this.isLabelErroredSubject.getValue();

		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);

		if (event.isValid) {
			const labelValue = event.value;
			const updated = currentHeaders.updateLabel(key, labelValue);
			this.availableHeadersSubject.next(updated);

			if (errorsLabels.has(key)) {
				const updatedErrors = errorsLabels.remove(key);
				this.isLabelErroredSubject.next(updatedErrors);
			}
			return;
		}

		if (!errorsLabels.has(key)) {
			const updatedErrors = errorsLabels.set(key, {
				key,
			});
			this.isLabelErroredSubject.next(updatedErrors);
		}
	}

	public async onSelectColumnType(
		key: number,
		event: ValueChangedEvent
	): Promise<void> {
		const selectedValue = event.value as ColumnType;
		if (!selectedValue) {
			return;
		}

		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);

		const elem = currentHeaders.elements.get(`${key}`);
		if (!elem) {
			return;
		}
		let element = elem.setColumnType(selectedValue);

		if (elem.format) {
			element = element.setFormat(null);
		}

		if (
			(elem.headerType === ColumnType.Date &&
				selectedValue === ColumnType.Str) ||
			(elem.headerType === ColumnType.Str && selectedValue === ColumnType.Date)
		) {
			element = element
				.setFormat(DefaultDateFormat)
				.setTimezone(DefaultTimezone)
				.setLanguage(DefaultLanguage);
		}

		if (
			(elem.headerType === ColumnType.Str &&
				selectedValue === ColumnType.Dec) ||
			(elem.headerType === ColumnType.Dec && selectedValue === ColumnType.Str)
		) {
			element = element.setFormat(DefaultNumberFormat);
		}
		const updated = currentHeaders.updateElement(`${key}`, element);
		this.availableHeadersSubject.next(updated);
	}

	public async onSelectFormat(
		key: string,
		event: ValueChangedEvent
	): Promise<void> {
		const selectedValue = event.value;
		if (!selectedValue) {
			return;
		}

		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);

		const elem = currentHeaders.elements.get(key.toString());
		if (!elem) {
			return;
		}
		const updatedElem = elem.setFormat(selectedValue);
		const updated = currentHeaders.updateElement(`${key}`, updatedElem);
		this.availableHeadersSubject.next(updated);
	}

	public async selectTimezone(
		key: number,
		event: ValueChangedEvent
	): Promise<void> {
		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);

		const selectedValue = event.value;
		if (!selectedValue) {
			return;
		}

		const elem = currentHeaders.elements.get(`${key}`);
		if (!elem) {
			return;
		}

		const updatedElem = elem.setTimezone(selectedValue);
		const updated = currentHeaders.updateElement(`${key}`, updatedElem);
		this.availableHeadersSubject.next(updated);
	}

	public async selectLanguage(
		key: number,
		event: ValueChangedEvent
	): Promise<void> {
		const selectedValue = event.value;
		if (!selectedValue) {
			return;
		}

		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);
		const elem = currentHeaders.elements.get(`${key}`);
		if (!elem) {
			return;
		}

		const updatedElem = elem.setLanguage(selectedValue);
		const updated = currentHeaders.updateElement(`${key}`, updatedElem);
		this.availableHeadersSubject.next(updated);
	}

	public async updateIsSelected(id: number, checked: boolean): Promise<void> {
		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);
		const updated = currentHeaders.updateSelection(id, checked);
		this.availableHeadersSubject.next(updated);

		const updatedListErrors = this.updateErrorList(updated.elements);

		if (updatedListErrors) {
			this.headerListErrorsSubject.next(updatedListErrors);
		}
	}

	public async updateIsList(id: number, checked: boolean): Promise<void> {
		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);

		const elem = currentHeaders.elements.get(`${id}`);
		if (!elem) {
			return;
		}
		const updatedElem = elem.setIsList(checked);
		const updated = currentHeaders.updateElement(`${id}`, updatedElem);
		this.availableHeadersSubject.next(updated);
	}

	public async toggleSelectAllRows(checked: boolean): Promise<void> {
		const currentHeaders = await firstValueFrom(this.availableHeadersSubject);
		const updated = currentHeaders.toggleAllSelection(checked);
		this.availableHeadersSubject.next(updated);

		const updatedListErrors = this.updateErrorList(updated.elements);

		if (updatedListErrors) {
			this.headerListErrorsSubject.next(updatedListErrors);
		}
	}

	public onMetadataError(isErrored: boolean): void {
		this.isMetadataErroredSubject.next(isErrored);
	}

	public toggleAllTriggered($event: MouseEvent): void {
		$event.stopPropagation();
	}

	public async onGridContentReady(): Promise<void> {
		this.readySubject$.next(true);

		const firstLoad = await firstValueFrom(this.firstLoadSubject);
		if (firstLoad) {
			this.firstLoadSubject.next(false);
		}
	}

	private updateErrorList(
		headerElements: Map<string, DepotHeaderItemEntity>
	): Map<HeaderErrorKey, HeaderListError> | null {
		const allUnselected = headerElements.every(
			(item: DepotHeaderItemEntity) => !item.isSelected
		);
		const currentErrors = this.headerListErrorsSubject.getValue();

		if (allUnselected) {
			if (currentErrors.has(HeaderErrorKey.NoHeaderSelected)) {
				return null;
			}
			return currentErrors.set(HeaderErrorKey.NoHeaderSelected, {
				key: HeaderErrorKey.NoHeaderSelected,
			});
		} else {
			if (!currentErrors.has(HeaderErrorKey.NoHeaderSelected)) {
				return null;
			}
			return currentErrors.remove(HeaderErrorKey.NoHeaderSelected);
		}
	}
}
